import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import '../../css/growthWheel.scss'
import {
  type GrowthWheelUserForReview,
  useGetGrowthWheelByRoleIdQuery,
  useGetGrowthWheelWithEvaluationsByUserIdQuery,
  useGetGrowthWheelUsersByGrowthWheelReviewerIdQuery,
} from '../../redux/growth-wheel.endpoints'
import {
  setGrowthWheelData,
  setUserCompetencyAreasData,
  setLoadingGrowthWheel,
  setCurrentCompetencyArea,
  setCurrentCompetency,
  evaluateFocusCompetenciesLimit,
  setCurrentAreasOfFocus,
} from '../../redux/growthWheel.slice'
import { RootState } from '../../redux/store'
import { User } from '../../types/User'
import { AdminContributorList } from '../../components/GrowthWheelComponents/AdminContributorList'
import BackButton from '../../components/GrowthWheelComponents/BackButton'
import GrowthWheelDashboard from '../GrowthWheelDashboard/GrowthWheelDashboard'
import GrowthWheelCompetencyAreaView from '../GrowthWheelCompetencyAreaView/GrowthWheelCompetencyAreaView'
import GrowthWheelCompetencyAssessment from '../GrowthWheelCompetencyView/GrowthWheelCompetencyView'
import { useSelector } from 'react-redux'

const GrowthWheelAdmin = ({ user }: { user: User }) => {
  const dispatch = useDispatch()
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [selectedReviewerGuildId, setselectedReviewerGuildId] = useState<
    number | null
  >(null)

  const { data: contributorsData } =
    useGetGrowthWheelUsersByGrowthWheelReviewerIdQuery(
      { reviewerId: user.id },
      { refetchOnMountOrArgChange: true },
    )

  const selectedUser = contributorsData?.find(c => c.id === selectedUserId)

  const roleId =
    selectedUserId != null
      ? (selectedReviewerGuildId ??
          selectedUser?.guildId ??
          user.guildId)
      : user.guildId

  const { data: growthWheelData, isLoading } = useGetGrowthWheelByRoleIdQuery(
    { roleId },
    { skip: !selectedUserId, refetchOnMountOrArgChange: true },
  )
  const { data: userCompetencyAreasData } =
    useGetGrowthWheelWithEvaluationsByUserIdQuery(
      { userId: selectedUserId! },
      { skip: !selectedUserId, refetchOnMountOrArgChange: true },
    )

  const currentCompetencyArea = useSelector(
    (state: RootState) => state.growthWheel.currentCompetencyArea,
  )
  const currentCompetency = useSelector(
    (state: RootState) => state.growthWheel.currentCompetency,
  )

  useEffect(() => {
    dispatch(setGrowthWheelData(growthWheelData ?? null))
  }, [dispatch, growthWheelData])

  useEffect(() => {
    dispatch(setUserCompetencyAreasData(userCompetencyAreasData ?? []))
    dispatch(evaluateFocusCompetenciesLimit())
    dispatch(setCurrentAreasOfFocus())
  }, [dispatch, userCompetencyAreasData])

  useEffect(() => {
    dispatch(setLoadingGrowthWheel(isLoading))
  }, [dispatch, isLoading])

  useEffect(() => {
    if (!userCompetencyAreasData?.length) return
    if (currentCompetencyArea) {
      const freshArea = userCompetencyAreasData.find(
        a => a.id === currentCompetencyArea.id,
      )
      if (freshArea) {
        dispatch(setCurrentCompetencyArea(freshArea))
        if (currentCompetency) {
          const freshCompetency = freshArea.competencies?.find(
            c => c.id === currentCompetency.id,
          )
          if (freshCompetency) dispatch(setCurrentCompetency(freshCompetency))
        }
      }
    }
  }, [
    userCompetencyAreasData,
    currentCompetencyArea,
    currentCompetency,
    dispatch,
  ])

  const handleSelectContributor = (contributor: GrowthWheelUserForReview) => {
    setSelectedUserId(contributor.id)
    setselectedReviewerGuildId(contributor.guildId)
    dispatch(setCurrentCompetencyArea(null))
    dispatch(setCurrentCompetency(null))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleBackToContributors = () => {
    setSelectedUserId(null)
    setselectedReviewerGuildId(null)
    dispatch(setCurrentCompetencyArea(null))
    dispatch(setCurrentCompetency(null))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const displayAssessmentView = currentCompetency && currentCompetencyArea
  const displayAreaView = currentCompetencyArea && !currentCompetency
  const displayDashboardView = !currentCompetencyArea && growthWheelData

  const reviewedUserName = selectedUser
    ? `${selectedUser.firstName} ${selectedUser.lastName}`
    : ''
  const reviewedGuildName = selectedUser?.area ?? ''

  return (
    <main>
      {!selectedUserId ? (
        <AdminContributorList
          contributors={contributorsData || []}
          onSelectContributor={handleSelectContributor}
        />
      ) : (
        <div className="container mb-5 gw-growth-wheel-container">
          {selectedUserId &&
            !currentCompetencyArea &&
            !currentCompetency && (
              <div className="my-5">
                <BackButton
                  onClick={handleBackToContributors}
                  text="Back to team members"
                />
              </div>
            )}

          {displayAssessmentView && (
            <GrowthWheelCompetencyAssessment
              isAdminView={true}
              userName={reviewedUserName}
              guildName={reviewedGuildName}
            />
          )}
          {displayAreaView && (
            <GrowthWheelCompetencyAreaView
              userName={reviewedUserName}
              guildName={reviewedGuildName}
            />
          )}
          {displayDashboardView && (
            <GrowthWheelDashboard
              userName={reviewedUserName}
              guildName={reviewedGuildName}
            />
          )}
        </div>
      )}
    </main>
  )
}

export default GrowthWheelAdmin
